import React, { FC, useEffect, useState, useCallback, useRef, useMemo } from "react";

import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import moment from "moment";

Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/kanit/v1/L6VKvM17ZmevDynOiw7H9w.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/kanit/v1/wfLWkj1C4tYl7MoiFWS3bA.ttf",
});

// ใช้ต่อท้ายข้อความไทยใน PDF เพื่อไม่ให้ตัวสุดท้ายถูกตัด (bug ของ @react-pdf/renderer)
const zw = "\u200B";

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportEmployeeCountByTypeListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ระดับพนักงาน",
      field: "level",
      order: null,
      isOrder: true,
    },
    {
      label: "ชาย",
      field: "male",
      order: null,
      isOrder: true,
    },
    {
      label: "หญิง",
      field: "female",
      order: null,
      isOrder: true,
    },
    {
      label: "รวม",
      field: "total",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result =
          await HrOnlineService.getReportEmployeeCountByTypeList(params);
        setData(result);
        setDataExport(result);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message,
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering_field, queryParams.ordering],
  );

  const tableRowNoData = () => {
    if (data.rows?.personnel_employees.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const displayedPersonnelSummary = useMemo(() => {
    const filtered = data.rows?.personnel_employees?.filter(
      (item: any) => item.level !== "11" && item.level !== "3"
    ) ?? [];
    return filtered.reduce(
      (acc: { male: number; female: number; total: number }, item: any) => ({
        male: acc.male + (item.male_count ?? 0),
        female: acc.female + (item.female_count ?? 0),
        total: acc.total + (item.total_count ?? 0),
      }),
      { male: 0, female: 0, total: 0 }
    );
  }, [data.rows]);

  const displayedTotalSummary = useMemo(() => ({
    male:
      displayedPersonnelSummary.male +
      (data.rows?.permanent_employees_summary?.male ?? 0) +
      (data.rows?.temporary_employees_summary?.male ?? 0),
    female:
      displayedPersonnelSummary.female +
      (data.rows?.permanent_employees_summary?.female ?? 0) +
      (data.rows?.temporary_employees_summary?.female ?? 0),
    total:
      displayedPersonnelSummary.total +
      (data.rows?.permanent_employees_summary?.total ?? 0) +
      (data.rows?.temporary_employees_summary?.total ?? 0),
  }), [displayedPersonnelSummary, data.rows]);

  const PDF: React.FC<{ data: any }> = ({ data }) => {
    const pdfPersonnelSummary = (() => {
      const filtered = data.rows?.personnel_employees?.filter(
        (item: any) => item.level !== "11" && item.level !== "3"
      ) ?? [];
      return filtered.reduce(
        (acc: { male: number; female: number; total: number }, item: any) => ({
          male: acc.male + (item.male_count ?? 0),
          female: acc.female + (item.female_count ?? 0),
          total: acc.total + (item.total_count ?? 0),
        }),
        { male: 0, female: 0, total: 0 }
      );
    })();
    const pdfTotalSummary = {
      male:
        pdfPersonnelSummary.male +
        (data.rows?.permanent_employees_summary?.male ?? 0) +
        (data.rows?.temporary_employees_summary?.male ?? 0),
      female:
        pdfPersonnelSummary.female +
        (data.rows?.permanent_employees_summary?.female ?? 0) +
        (data.rows?.temporary_employees_summary?.female ?? 0),
      total:
        pdfPersonnelSummary.total +
        (data.rows?.permanent_employees_summary?.total ?? 0) +
        (data.rows?.temporary_employees_summary?.total ?? 0),
    };
    return (
    <Document>
      <Page size="A4" style={{ padding: 10 }}>
        <View>
          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 16,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"องค์การตลาดเพื่อเกษตรกร" + zw}
          </Text>

          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 16,
              textAlign: "center",
              width: "100%",
              marginBottom: 1,
            }}
          >
            {"รายงานจำนวนพนักงานและลูกจ้าง" + zw}
          </Text>

          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 16,
              textAlign: "center",
              marginBottom: 1,
            }}
          >
            {"ประจำวันที่" + zw}{" "}
            {moment().format("D") +
              " " +
              thaiMonths[moment().month()] +
              " " +
              (moment().year() + 543) +
              zw}
          </Text>

          {/* ตาราง - ครอบทั้งตารางด้วย View เดียว */}
          <View
            style={{
              border: "1px solid black",
              borderBottom: "1px solid black",
            }}
          >
            {/* หัวตาราง */}
            <View
              style={{
                flexDirection: "row",
                borderBottom: "1px solid black",

                fontFamily: "kanit-regular",
                fontSize: 11,
              }}
              fixed
            >
              <Text
                style={{
                  width: "25%",
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {"ระดับพนักงาน" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {"ชาย" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                }}
              >
                {"หญิง" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  textAlign: "center",
                  padding: 5,
                }}
              >
                {"รวม" + zw}
              </Text>
            </View>

            {/* ข้อมูลพนักงาน */}
            {data.rows?.personnel_employees.map((item: any, index: number) => (
              item.level !== "11" && item.level !== "3" && ( <>
              <View
                key={item.level}
                style={{
                  flexDirection: "row",
                  borderBottom: "1px solid black",
                }}
              >
                <Text
                  style={{
                    width: "25%",
                    fontSize: 10,
                    textAlign: "left",
                    padding: 5,
                    borderRight: "1px solid black",
                    fontFamily: "kanit-200",
                  }}
                >
                  {"ระดับ " + item.level + zw}
                </Text>
                <Text
                  style={{
                    width: "25%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                    fontFamily: "kanit-200",
                  }}
                >
                  {item.male_count}
                </Text>
                <Text
                  style={{
                    width: "25%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    borderRight: "1px solid black",
                    fontFamily: "kanit-200",
                  }}
                >
                  {item.female_count}
                </Text>
                <Text
                  style={{
                    width: "25%",
                    fontSize: 10,
                    textAlign: "center",
                    padding: 5,
                    fontFamily: "kanit-200",
                  }}
                >
                  {item.total_count}
                </Text>
              </View>
              </>
            )))}

            {/* รวมพนักงาน */}
            <View
              style={{
                flexDirection: "row",
                borderBottom: "1px solid black",
              }}
            >
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {"รวม" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {pdfPersonnelSummary.male}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {pdfPersonnelSummary.female}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  fontFamily: "kanit-200",
                }}
              >
                {pdfPersonnelSummary.total}
              </Text>
            </View>

            {/* ลูกจ้างประจำ - ใช้ kanit-regular เพื่อให้สระ ำ แสดงครบ */}
            <View
              style={{
                flexDirection: "row",
                borderBottom: "1px solid black",
              }}
            >
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {"ลูกจ้างประจำ" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {data.rows?.permanent_employees_summary?.male}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {data.rows?.permanent_employees_summary?.female}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  fontFamily: "kanit-200",
                }}
              >
                {data.rows?.permanent_employees_summary?.total}
              </Text>
            </View>

            {/* ลูกจ้างชั่วคราว */}
            <View
              style={{
                flexDirection: "row",
              }}
            >
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "left",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {"ลูกจ้างชั่วคราว" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {data.rows?.temporary_employees_summary?.male}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  borderRight: "1px solid black",
                  fontFamily: "kanit-200",
                }}
              >
                {data.rows?.temporary_employees_summary?.female}
              </Text>
              <Text
                style={{
                  width: "25%",
                  fontSize: 10,
                  textAlign: "center",
                  padding: 5,
                  fontFamily: "kanit-200",
                }}
              >
                {data.rows?.temporary_employees_summary?.total}
              </Text>
            </View>

            {/* รวมทั้งหมด */}
            <View
              style={{
                flexDirection: "row",
                fontFamily: "kanit-regular",
                fontSize: 10,
                borderTop: "1px solid black",
                borderBottom: "1px solid black",
              }}
            >
              <Text
                style={{
                  width: "25%",
                  textAlign: "left",
                  padding: 5,
                  fontWeight: "bold",
                  borderRight: "1px solid black",
                }}
              >
                {"รวม" + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  textAlign: "center",
                  padding: 5,
                  fontWeight: "bold",
                  borderRight: "1px solid black",
                }}
              >
                {pdfTotalSummary.male + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  textAlign: "center",
                  padding: 5,
                  fontWeight: "bold",
                  borderRight: "1px solid black",
                }}
              >
                {pdfTotalSummary.female + zw}
              </Text>
              <Text
                style={{
                  width: "25%",
                  textAlign: "center",
                  padding: 5,
                  fontWeight: "bold",
                }}
              >
                {pdfTotalSummary.total}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
  };

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">รายงานจำนวนพนักงานและลูกจ้าง</h2>
      </div>
      {loaderComponent}
      <div className="row">
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานจำนวนพนักงานและลูกจ้าง_${moment().format(
              "DDMMYYYY",
            )}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary me-2"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.personnel_employees.map((data: any, index: any) => {
              return <ReportEmployeeCountByTypeRow key={index} {...data} />;
            })}

            <tr>
              <td>รวม</td>
              <td>{displayedPersonnelSummary.male}</td>
              <td>{displayedPersonnelSummary.female}</td>
              <td>{displayedPersonnelSummary.total}</td>
            </tr>
            <tr>
              <td>ลูกจ้างประจำ</td>
              <td>{data.rows?.permanent_employees_summary?.male}</td>
              <td>{data.rows?.permanent_employees_summary?.female}</td>
              <td>{data.rows?.permanent_employees_summary?.total}</td>
            </tr>
            <tr>
              <td>ลูกจ้างชั่วคราว</td>
              <td>{data.rows?.temporary_employees_summary?.male}</td>
              <td>{data.rows?.temporary_employees_summary?.female}</td>
              <td>{data.rows?.temporary_employees_summary?.total}</td>
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <td>รวมทุกระดับ</td>
              <td>{displayedTotalSummary.male}</td>
              <td>{displayedTotalSummary.female}</td>
              <td>{displayedTotalSummary.total}</td>
            </tr>
            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const ReportEmployeeCountByTypeRow: FC<any> = ({
  level,
  male_count,
  female_count,
  total_count,
}) => {
  return (
    <>
      {level !== "11" && level !== "3" && (
        <tr>
          <td>ระดับ {level}</td>
          <td>{male_count}</td>
          <td>{female_count}</td>
          <td>{total_count}</td>
        </tr>
      )}
    </>
  );
};

export default ReportEmployeeCountByTypeListPage;
